import {useCallback, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {LocalStorage} from '@chekin/core';
import {useChekinSDK} from 'context/ChekinSDK';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {guestGroupQueryOptions} from 'hooks/useGuestGroup';
import {isTerminalSession} from 'hooks/terminalMode';

const doneKey = (reservationId: string) =>
  `chekin-instant-check-in-done-${reservationId}`;

function skipInstantCheckIn(reservationId: string) {
  LocalStorage.set(doneKey(reservationId), '1');
}

function useInstantCheckInOffer() {
  const {isSuccessSDK} = useChekinSDK();
  const {
    isInstantCheckInEnabled,
    summaryStatus,
    reservationId,
    isLoading: isSummaryLoading,
  } = useSummary();
  const {data: reservation, isLoading: isReservationLoading} = useReservation();
  const guestGroupId = reservation?.guest_group_id;
  const [, setDoneVersion] = useState(0);

  const guestGroup = useQuery({
    ...guestGroupQueryOptions(guestGroupId),
    select: group => group.added_number_of_guests,
    enabled: isSuccessSDK && isInstantCheckInEnabled && Boolean(guestGroupId),
  });

  const isSummarySettled = summaryStatus === 'success' || summaryStatus === 'error';
  const isGuestGroupSettled =
    guestGroup.isSuccess ||
    guestGroup.isError ||
    (!isReservationLoading && !guestGroupId);
  const isDecided = isSummarySettled && (!isInstantCheckInEnabled || isGuestGroupSettled);

  const isDeciding =
    !isDecided && (isSummaryLoading || isReservationLoading || guestGroup.isLoading);

  const isDone = Boolean(reservationId && LocalStorage.get(doneKey(reservationId)));
  const shouldOffer =
    isDecided &&
    isInstantCheckInEnabled &&
    !isDone &&
    !isTerminalSession() &&
    guestGroup.data === 0;

  const markDone = useCallback(() => {
    if (reservationId) {
      skipInstantCheckIn(reservationId);
      setDoneVersion(version => version + 1);
    }
  }, [reservationId]);

  return {isDecided, isDeciding, shouldOffer, markDone};
}

export {skipInstantCheckIn, useInstantCheckInOffer};
